/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

/**
 * React Video Thumbnail Component
 * @author mike trieu
 */
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import './VideoThumbnail.scss';
import { playIcon } from 'shared/assets/icons';

/**
 * Simple component that renders thumbnail url
 * @param {string} snapshot
 */
const ThumbnailImage = ({ snapshot, onClickHandle }) => {
    return (
        <div className="react-thumbnail-generator d-flex align-items-center justify-content-center" >
            <span className=" clickable" style={{ position: 'absolute', zIndex: '1' }}  onClick={onClickHandle}>{playIcon}</span>
                {/* <i className="far fa-play-circle fa-3x text-white clickable" style={{ position: 'absolute', zIndex: '1' }}
                    onClick={onClickHandle}></i> */}
            <img src={snapshot} alt="my video thumbnail" />
        </div>
    );
}

export default class VideoThumbnail extends React.Component {

    constructor(props) {
        super(props);
        this.videoEl = createRef();
        this.canvas = createRef();

        this.state = {
            dataLoaded: false,      // boolean
            metadataLoaded: false,  // boolean
            seeked: false,          // boolean
            snapshot: false,        // string thumbnail url || false
            suspended: false,       // boolean
            // props
            cors: props.cors,                           // boolean
            width: props.width,                         // number
            height: props.height,                       // number
            renderThumbnail: props.renderThumbnail,     // boolean
            snapshotAtTime: props.snapshotAtTime,       // number
            thumbnailHandler: props.thumbnailHandler,   // callback function
            videoUrl: props.videoUrl,                   // string
        }
    }

    onClickHandle = () => {
        this.props.onClick ? this.props.onClick(true) : console.error('OnClick disabled');
    }

    onPlay = () => {
        setTimeout(() => {

            var isInFullScreen = (document.fullscreenElement && document.fullscreenElement !== null) ||
                (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) ||
                (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
                (document.msFullscreenElement && document.msFullscreenElement !== null);

            if (isInFullScreen) {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }, 3000);

    }
    render() {
        const { renderThumbnail, snapshot, videoUrl } = this.state;
        if (!snapshot) {
            return (
                <div className="react-thumbnail-generator" >
                    <canvas className="snapshot-generator" ref={this.canvas} ></canvas>
                    <video
                        className="snapshot-generator"
                        ref={this.videoEl}
                        src={this.props.videoUrl}
                        key={videoUrl}
                        onLoadedMetadata={() => this.setState({ metadataLoaded: true })}
                        onLoadedData={() => this.setState({ dataLoaded: true })}
                        onSuspend={() => this.setState({ suspended: true })}
                        onSeeked={() => this.setState({ seeked: true })}
                        onPlay={this.onPlay}
                        autoPlay={true}
                        loop
                        muted
                        preload="metadata"
                        playsinline
                        webkit-playsinline // for older ios versions
                    >
                    </video>
                </div>
            )
        } else {
            if (renderThumbnail) {
                return <ThumbnailImage snapshot={snapshot} onClickHandle={this.onClickHandle} />;
            } else {
                return null;
            }
        }
    }

    /**
    * Update any props that may have changed
    */
    componentWillReceiveProps(nextProps) {
        let stateChanged = false;
        const data = {};
        for (let prop in nextProps) {
            if (nextProps[prop] !== this.props[prop]) {
                data[prop] = nextProps[prop];
                if (!stateChanged) {
                    stateChanged = true;
                }
            }
        }
        if (stateChanged) {
            this.setState(data);
        }
    }

    componentDidMount() {
        if (!this.state.cors) this.videoEl.current.setAttribute('crossOrigin', 'Anonymous');
        // console.log('mount state: ', this.state)
    }

    /**
     * (fires every time setState() gets called)
     */
    componentDidUpdate(prevProps, prevState) {
        if (!this.state.snapshot) {
            const { metadataLoaded, dataLoaded, suspended, seeked, snapshot, snapshotAtTime } = this.state;

            // check if all 3 required events fired
            if (metadataLoaded && dataLoaded && suspended) {
                if (!this.videoEl.current.currentTime || this.videoEl.current.currentTime < this.state.snapshotAtTime) {
                    this.videoEl.current.currentTime = snapshotAtTime;
                }

                if (seeked && !snapshot) {
                    // attempt to generate thumbnail
                    this.getSnapShot();
                }
            }
        }
    }

    /**
     * Create a canvas and video element to "draw" the
     * image, then convert it to a data url
     */
    getSnapShot = () => {
        try {
            const { width, height } = this.props;
            const video = this.videoEl.current;
            const canvas = this.canvas.current;
            canvas.height = video.videoHeight;
            canvas.width = video.videoWidth;

            // resize thumbnail or no ?
            if (!width || !height) {
                canvas.getContext('2d').drawImage(video, 0, 0);
            } else {
                canvas.getContext('2d').drawImage(video, 0, 0, width, height);
            }

            const thumbnail = canvas.toDataURL('image/png');

            // Remove video & canvas elements (no longer needed)
            video.src = "";  // setting to empty string stops video from loading
            video.remove();
            canvas.remove();

            this.setState({
                snapshot: thumbnail
            })

            // pass the thumbnail url back to parent component's thumbnail handler (if any)
            if (this.state.thumbnailHandler) {
                this.state.thumbnailHandler(thumbnail);
            }

        } catch (e) {
            console.error(e);
        }
    }
}

/**
 * Property Types
 */
VideoThumbnail.propTypes = {
    cors: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    renderThumbnail: PropTypes.bool,
    snapshotAtTime: PropTypes.number,
    thumbnailHandler: PropTypes.func,
    videoUrl: PropTypes.string.isRequired,
}

/**
 * Default Properties
 */
VideoThumbnail.defaultProps = {
    cors: false,
    renderThumbnail: true,
    snapshotAtTime: 2,
}